/**
 * The accounting posting ledger (ADR-0028 Part A), pinned in one file.
 *
 * Read directly off `Tarabeeza-Backend/src/modules/accounting/{accounting-postings.controller.ts,
 * accounting-postings.service.ts,dto/index.ts,domain/canonical-posting.ts}` and
 * `prisma/schema.prisma`'s `AccountingPosting`/`AccountingPostingKind`/
 * `AccountingPostingStatus` — not guessed, so a later backend change is a diff
 * to this file rather than a hunt through every screen that reads it.
 *
 * THE ONE THING THIS MODULE DOES NOT HAVE: a `/accounting/reconciliation`
 * summary endpoint. Fiscal (ADR-0005) has one and this screen's sibling,
 * `Pages/Fiscal/components/ReconciliationPanel.tsx`, leans on it for four
 * venue-wide counts. Nothing here fabricates that summary from a paginated
 * page of rows — a count over 20 loaded rows presented as "3 failed" would be
 * a true sentence about the page and a false one about the venue, which is
 * exactly the empty-vs-broken lie this whole audit is about avoiding. So this
 * screen filters (kind, status, branch) instead of summarising.
 *
 * ONLY TWO STATUSES ARE THIS MODULE'S OWN (`AccountingPostingStatus`):
 * `QUEUED` and `REVERSED`. Whether a QUEUED posting has actually reached a
 * ledger is never a third value mirrored onto the posting row — it is read
 * LIVE off `integration_event_deliveries` (ADR-0028 §A1: "a cached copy is a
 * copy that can disagree with the row it was copied from"), which is why
 * every posting the API hands back carries a `deliveries[]` array alongside
 * its own two-value `status`. `deriveStatus` in `./lib` is what turns that
 * pair into the one badge an operator reads — see its own comment for why a
 * *posting* that failed and one that has not run yet must never share a tone.
 */

// ---------------------------------------------------------------------------
// enums — mirrors prisma/schema.prisma exactly
// ---------------------------------------------------------------------------

export const AccountingPostingKind = {
  SALE: 'SALE',
  PAYOUT: 'PAYOUT',
  REVERSAL: 'REVERSAL',
} as const;
export type AccountingPostingKind =
  (typeof AccountingPostingKind)[keyof typeof AccountingPostingKind];

/** The only two transitions `AccountingPostingsService` itself writes. */
export const AccountingPostingStatus = {
  QUEUED: 'QUEUED',
  REVERSED: 'REVERSED',
} as const;
export type AccountingPostingStatus =
  (typeof AccountingPostingStatus)[keyof typeof AccountingPostingStatus];

/** `IntegrationDeliveryStatus` — the live, per-installation half of the story. */
export const IntegrationDeliveryStatus = {
  PENDING: 'PENDING',
  DELIVERING: 'DELIVERING',
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
  DEAD_LETTERED: 'DEAD_LETTERED',
  CANCELLED: 'CANCELLED',
} as const;
export type IntegrationDeliveryStatus =
  (typeof IntegrationDeliveryStatus)[keyof typeof IntegrationDeliveryStatus];

// ---------------------------------------------------------------------------
// routes
// ---------------------------------------------------------------------------

export const ACCOUNTING_ROUTES = {
  postings: '/accounting/postings',
  posting: (id: string) => `/accounting/postings/${id}`,
  reverse: (id: string) => `/accounting/postings/${id}/reverse`,
  buildForCheck: (checkId: string) => `/accounting/checks/${checkId}/posting`,
} as const;

/**
 * The permission keys `AccountingPostingsController` actually enforces
 * (`permission-catalogue.json`), so this screen never offers a control
 * guaranteed to answer 403. Three keys, not one `accounting.manage`: reading
 * the ledger, building a posting by hand, and reversing one are three
 * different kinds of trust — the same split `FISCAL_PERMISSIONS` makes
 * between `retry` and `cancel`.
 */
export const ACCOUNTING_PERMISSIONS = {
  view: 'accounting.view',
  post: 'accounting.post',
  reverse: 'accounting.reverse',
} as const;

// ---------------------------------------------------------------------------
// shapes
// ---------------------------------------------------------------------------

/** One installation's live delivery record for a posting — `withDeliveryStatus`'s own select. */
export interface AccountingPostingDelivery {
  installationId: string;
  status: IntegrationDeliveryStatus;
  attempts: number;
  lastError: string | null;
  deliveredAt: string | null;
}

export type PostingGlBucket = 'REVENUE' | 'SERVICE_CHARGE' | 'DISCOUNT' | 'TAX' | 'TIP';

export interface CanonicalPostingLine {
  lineNumber: number;
  description: string;
  catalogItemId: string | null;
  glBucket: PostingGlBucket;
  quantity: string;
  unitPrice: string;
  netAmount: string;
  taxAmount: string;
  totalAmount: string;
  sourceChargeId: string;
}

export interface CanonicalPostingTaxBucket {
  rate: string | null;
  amount: string;
}

export interface CanonicalPostingTender {
  method: string;
  amount: string;
}

export interface CanonicalSalePosting {
  kind: 'SALE';
  currency: { code: string; decimals: number };
  issuedAt: string;
  branchId: string;
  checkId: string;
  checkNumber: string;
  sessionId: string | null;
  lines: CanonicalPostingLine[];
  taxTotals: CanonicalPostingTaxBucket[];
  totals: {
    net: string;
    discount: string;
    service: string;
    tax: string;
    tip: string;
    grand: string;
  };
  tenders: CanonicalPostingTender[];
}

export interface CanonicalPayoutPosting {
  kind: 'PAYOUT';
  currency: { code: string; decimals: number };
  issuedAt: string;
  branchId: string;
  shiftId: string;
  cashierUserId: string;
  cashOut: string;
  movements: { id: string; amount: string; reason: string }[];
}

export interface CanonicalReversalPosting {
  kind: 'REVERSAL';
  currency: { code: string; decimals: number };
  issuedAt: string;
  branchId: string;
  reverses: { postingId: string; identity: string };
  original: CanonicalSalePosting | CanonicalPayoutPosting;
}

export type CanonicalAccountingPosting =
  | CanonicalSalePosting
  | CanonicalPayoutPosting
  | CanonicalReversalPosting;

/** One row of `GET /accounting/postings`. */
export interface AccountingPostingRow {
  id: string;
  tenantId: string;
  branchId: string;
  kind: AccountingPostingKind;
  status: AccountingPostingStatus;
  checkId: string | null;
  shiftId: string | null;
  reversesId: string | null;
  identity: string;
  /** A Decimal string — see the app-wide money rule. Never `parseFloat` this. */
  amount: string;
  currency: string;
  payload: CanonicalAccountingPosting;
  createdAt: string;
  queuedAt: string;
  reversedAt: string | null;
  reversedById: string | null;
  deliveries: AccountingPostingDelivery[];
}

export interface ListAccountingPostingsQuery {
  status?: AccountingPostingStatus;
  kind?: AccountingPostingKind;
  branchId?: string;
  checkId?: string;
  limit?: number;
  skip?: number;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** Surface the API's own message rather than a generic failure line. */
export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  return fallback;
}

/** The HTTP status of a failed request, when there was a response at all. */
export function errorStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
}
